import plur from 'plur';
import type { NewslettersNewsletterScheduleResponse } from '@customerio/hydra-ui-api';
import { queryDecode } from '@/utils/condition-query-encoding';
import { isEmptyString } from '@/utils/string';
import type { Newsletter } from './entity';
import type { NewsletterRecipientCounts } from './queries';
import { getNewsletterChannelInfo } from './channel-info';

export type NewsletterSendState = Newsletter['send_state'];
export type NewsletterScheduleContext = {
  schedule?: NewslettersNewsletterScheduleResponse['schedule'];
};
export type NewsletterSendStateContext = NewsletterScheduleContext & { now: number };
export type NewsletterPauseContext = { pauseResumeEnabled: boolean };
export type NewsletterResumeContext = NewsletterPauseContext & { now: number };
export type NewsletterAudienceContext = {
  subscriptionCenterEnabled: boolean;
  subscriptionTopicExists: boolean;
};
export type NewsletterRecipientContext = {
  recipientFileUploadEnabled: boolean;
  isFreeBuilder: boolean;
};
export type NewsletterRecipientsContext = NewsletterAudienceContext &
  NewsletterRecipientContext & { recipientCountsLoading: boolean; totalRecipients: number };
export type NewsletterChannelContext = { subscriptionCenterEnabled: boolean };
export type NewsletterTestContext = { testVariationCount: number };

type SendStateNewsletter = Pick<
  Newsletter,
  | 'sent_at'
  | 'scheduled_at'
  | 'cancelled_at'
  | 'failed'
  | 'is_paused'
  | 'sending'
  | 'send_percentage'
  | 'test_ended_at'
  | 'sent_to_remaining_at'
  | 'automatic_winner'
>;
type RecipientNewsletter = Pick<
  Newsletter,
  'send_to_everyone' | 'recipient_file_status' | 'test_recipient_internal_ids' | 'filters'
>;

/** @public */
export function isSent(newsletter: Pick<Newsletter, 'sent_at'>): boolean {
  return Boolean(newsletter.sent_at);
}

/** @public */
export function isScheduled(
  newsletter: Pick<Newsletter, 'scheduled_at'>,
  { schedule }: NewsletterScheduleContext = {},
): boolean {
  return Boolean(newsletter.scheduled_at ?? schedule?.scheduled_at);
}

/** @public */
export function editable(newsletter: Pick<Newsletter, 'sent_at'>): boolean {
  return newsletter.sent_at == null;
}

/** @public */
export function sendState(newsletter: SendStateNewsletter, context: NewsletterSendStateContext): NewsletterSendState {
  if (newsletter.cancelled_at != null && newsletter.cancelled_at !== 0) return 'stopped';
  if (newsletter.failed === true) return 'failed';
  if (newsletter.is_paused === true) return 'paused';
  if (isSent(newsletter)) {
    const testEnded = Boolean(newsletter.test_ended_at) || Boolean(newsletter.sent_to_remaining_at);
    const testWindowExpired = newsletter.sent_at != null && context.now - newsletter.sent_at > 30 * 86_400;
    if (!testEnded && newsletter.send_percentage < 100 && !newsletter.automatic_winner && !testWindowExpired) {
      return 'awaiting_winner';
    }
    return newsletter.sending ? 'sending' : 'sent';
  }
  return isScheduled(newsletter, context) ? 'scheduled' : 'drafted';
}

/** The states a send can be held in while its audience segments finish building. */
const QUEUEABLE_SEND_STATES: readonly NewsletterSendState[] = ['scheduled', 'sending', 'awaiting_winner'];

/**
 * Ember `NewsletterStateTag#isQueued` (FLO-4596): the backend holds the send until its audience
 * segments finish building. `send_state` is unchanged meanwhile, and `scheduled_at` holds the next
 * readiness check rather than a send time. Terminal states (paused, stopped, failed) stay visible.
 *
 * @public
 */
export function isQueued(newsletter: Pick<Newsletter, 'waiting_for_segments'>, state: NewsletterSendState): boolean {
  return newsletter.waiting_for_segments === true && QUEUEABLE_SEND_STATES.includes(state);
}

/** @public */
export function canPause(
  newsletter: Pick<Newsletter, 'cancelled_at' | 'failed' | 'tz_match_enabled' | 'is_paused' | 'sending'>,
  { pauseResumeEnabled }: NewsletterPauseContext,
): boolean {
  return (
    pauseResumeEnabled &&
    newsletter.cancelled_at == null &&
    newsletter.failed !== true &&
    !newsletter.tz_match_enabled &&
    newsletter.is_paused !== true &&
    newsletter.sending
  );
}

/** @public */
export function canResume(
  newsletter: Pick<Newsletter, 'cancelled_at' | 'failed' | 'sent_at' | 'is_paused'>,
  { pauseResumeEnabled, now }: NewsletterResumeContext,
): boolean {
  return (
    pauseResumeEnabled &&
    newsletter.cancelled_at == null &&
    newsletter.failed !== true &&
    (newsletter.sent_at == null || now - newsletter.sent_at <= 60 * 86_400) &&
    newsletter.is_paused === true
  );
}

/** @public */
export function channelInfo(
  newsletter: Pick<Newsletter, 'channel' | 'conversion'>,
  { subscriptionCenterEnabled }: NewsletterChannelContext,
) {
  const info = getNewsletterChannelInfo(newsletter.channel);
  return {
    ...info,
    metrics: info.metrics.filter(
      (metric) =>
        (subscriptionCenterEnabled || metric !== 'topic_unsubscribed') &&
        (newsletter.channel !== 'webhook' || newsletter.conversion === true || metric !== 'converted'),
    ),
    abMetrics: [...info.abMetrics],
  };
}

/** @public */
export function hasTestSetup(
  newsletter: Pick<Newsletter, 'send_percentage' | 'automatic_winner'>,
  { testVariationCount }: NewsletterTestContext,
): boolean {
  return testVariationCount > 1 || newsletter.send_percentage < 100 || newsletter.automatic_winner;
}

export type NewsletterRecipientType = 'everyone' | 'recipient-file' | 'test-recipients' | 'subset';

/** @public */
export function recipientType(
  newsletter: RecipientNewsletter,
  { recipientFileUploadEnabled, isFreeBuilder }: NewsletterRecipientContext,
): NewsletterRecipientType | undefined {
  if (newsletter.send_to_everyone === true) return 'everyone';
  if (recipientFileUploadEnabled && !isEmptyString(newsletter.recipient_file_status)) {
    return 'recipient-file';
  }
  if (isFreeBuilder && (newsletter.test_recipient_internal_ids?.length ?? 0) > 0) return 'test-recipients';
  return hasAudienceFilters(newsletter.filters) ? 'subset' : undefined;
}

/** @public */
export function hasValidAudience(
  newsletter: Pick<Newsletter, 'send_to_unsubscribed'>,
  { subscriptionCenterEnabled, subscriptionTopicExists }: NewsletterAudienceContext,
): boolean {
  return !subscriptionCenterEnabled || subscriptionTopicExists || newsletter.send_to_unsubscribed;
}

/** @public */
export function hasRecipients(
  newsletter: RecipientNewsletter & Pick<Newsletter, 'send_to_unsubscribed'>,
  context: NewsletterRecipientsContext,
): boolean {
  const type = recipientType(newsletter, context);
  if (type === undefined || !hasValidAudience(newsletter, context)) return false;
  if (type === 'test-recipients') return true;
  if (type === 'recipient-file' && newsletter.recipient_file_status !== 'done') return false;
  return context.recipientCountsLoading || context.totalRecipients > 0;
}

function hasAudienceFilters(filters: Newsletter['filters']): boolean {
  if (isEmptyString(filters)) return false;
  try {
    const decoded = queryDecode(filters);
    return Array.isArray(decoded) ? decoded.length > 0 : decoded != null;
  } catch {
    return false;
  }
}

export type NewsletterRecipientCountContext = {
  recipientType: NewsletterRecipientType | undefined;
  recipientCounts: NewsletterRecipientCounts | undefined;
};

export function normalizeNewsletterRecipientCounts(
  counts: NewsletterRecipientCounts | undefined,
): NewsletterRecipientCounts {
  return counts ?? { total: 0, sto: 0, deduped: 0, created: 0, timezoneMisconfigured: 0 };
}

export function recipientsCount(
  newsletter: Pick<Newsletter, 'sent_at' | 'send_percentage' | 'sent_to_remaining_at' | 'test_recipient_internal_ids'>,
  { recipientType: type, recipientCounts }: NewsletterRecipientCountContext,
): number {
  if (type === 'test-recipients') return newsletter.test_recipient_internal_ids?.length ?? 0;
  const counts = normalizeNewsletterRecipientCounts(recipientCounts);
  if (isSent(newsletter)) {
    if (newsletter.send_percentage < 100) {
      const sentToRemaining = newsletter.sent_to_remaining_at != null && newsletter.sent_to_remaining_at !== 0;
      return sentToRemaining ? counts.created : counts.total - counts.deduped;
    }
    return counts.created;
  }
  return counts.total;
}

export function includesUnsubscribed(
  newsletter: Pick<Newsletter, 'send_to_unsubscribed' | 'channel' | 'subscription_topic_id'>,
  { subscriptionCenterEnabled }: NewsletterChannelContext,
): boolean {
  if (newsletter.send_to_unsubscribed === true) return true;
  if (getNewsletterChannelInfo(newsletter.channel ?? 'unset').ignoresGlobalUnsubscribe !== true) return false;
  return !(subscriptionCenterEnabled && newsletter.subscription_topic_id != null);
}

export function isDeduped(newsletter: Pick<Newsletter, 'deduped' | 'channel'>): boolean {
  return newsletter.deduped && ['email', 'unset', 'twilio', 'sms'].includes(newsletter.channel);
}

function isMaybeChannel(newsletter: Pick<Newsletter, 'channel'>, channel: Newsletter['channel']): boolean {
  return newsletter.channel == null || newsletter.channel === 'unset' || newsletter.channel === channel;
}

export function canDedupEmail(
  newsletter: Pick<Newsletter, 'channel'>,
  { canIdentifyByEmail }: { canIdentifyByEmail: boolean },
): boolean {
  return !canIdentifyByEmail && isMaybeChannel(newsletter, 'email');
}

/** Both SMS channels, which the workspace's quiet hours apply to. */
export function isSmsNewsletter(newsletter: Pick<Newsletter, 'channel'>): boolean {
  return newsletter.channel === 'twilio' || newsletter.channel === 'sms';
}

export function canDedupPhone(
  newsletter: Pick<Newsletter, 'channel'>,
  { enabledActions, canIdentifyByPhone }: { enabledActions: readonly string[]; canIdentifyByPhone: boolean },
): boolean {
  const smsEnabled = enabledActions.includes('twilio') || enabledActions.includes('sms');
  return (
    !canIdentifyByPhone && smsEnabled && (isMaybeChannel(newsletter, 'twilio') || isMaybeChannel(newsletter, 'sms'))
  );
}

export type DedupContext = {
  enabledActions: readonly string[];
  canIdentifyByEmail: boolean;
  canIdentifyByPhone: boolean;
};

/**
 * Which duplicate lists an unsent send still removes. The flag outlives the
 * workspace turning an identifier on, where duplicates can't exist, so it only
 * counts for the lists the setup step would offer.
 */
export function dedupApplies(newsletter: Pick<Newsletter, 'deduped' | 'channel'>, context: DedupContext) {
  return {
    email: newsletter.deduped && canDedupEmail(newsletter, context),
    phone: newsletter.deduped && canDedupPhone(newsletter, context),
  };
}

export function sendingTotalPeople(
  newsletter: Parameters<typeof recipientsCount>[0],
  context: NewsletterRecipientCountContext,
): number {
  return Math.round((recipientsCount(newsletter, context) * newsletter.send_percentage) / 100);
}

export function describeSendExpiry(seconds: Newsletter['expires_after']): string {
  if (seconds == null || seconds <= 0) return '';
  if (seconds % 3600 === 0) {
    const hours = seconds / 3600;
    return `${hours} ${plur('hour', hours)}`;
  }
  if (seconds % 60 === 0) {
    const minutes = seconds / 60;
    return `${minutes} ${plur('minute', minutes)}`;
  }
  return `${seconds} ${plur('second', seconds)}`;
}
